import {Injectable} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {AreYouSureComponent} from "../../modals/common/are-you-sure/are-you-sure.component";
import {firstValueFrom} from "rxjs";
import {centeredModal, lg, xl, xl2} from "../../../../assets/config/modals";
import {ComputationReferenceInterface} from "../../interfaces/computation/computation-reference.interface";
import {ComputationExampleComponent} from "../../modals/computation-example/computation-example.component";
import {SuggestComputationComponent} from "../../modals/common/suggest-computation/suggest-computation.component";
import {LimitReachedComponent} from "../../modals/common/limit-reached/limit-reached.component";
import {OutputPathInfoComponent} from "../../modals/info/output-path-info/output-path-info.component";
import {InfoAuthTokenComponent} from "../../modals/info/info-auth-token/info-auth-token.component";
import {TierInterface} from "../../interfaces/billing/tier.Interface";
import {
  ChangeTierConfirmationComponent
} from "../../modals/common/change-tier-confirmation/change-tier-confirmation.component";
import {InfoMatchValueOnComponent} from "../../modals/info/info-match-value-on/info-match-value-on.component";
import {InfoUnwrapObjectComponent} from "../../modals/info/info-unwrap-object/info-unwrap-object.component";
import {ErrorModalComponent} from "../../modals/common/error-modal/error-modal.component";
import {HowToPostDataComponent} from "../../modals/info/how-to-post-data/how-to-post-data.component";
import {ModalInfoWhatIsComponent} from "../../modals/info/modal-info-what-is/modal-info-what-is.component";
import {InfoSlackChannelComponent} from "../../modals/info/info-slack-channel/info-slack-channel.component";
import {
  InfoStandardIntegrationComponent
} from "../../modals/info/info-standard-integration/info-standard-integration.component";
import {
  InfoAdditionalCostDetailsComponent
} from "../../modals/info/info-additional-cost-details/info-additional-cost-details.component";
import {AdditionalCostsInterface} from "../../interfaces/workspace/additional-costs.interface";
import {GetInTouchComponent} from "../../modals/common/get-in-touch/get-in-touch.component";
import {
  InfoBuiltInIntegrationsNotAllowedComponent
} from "../../modals/info/info-built-in-integrations-not-allowed/info-built-in-integrations-not-allowed.component";
import {AiCodeAssistComponent, AiCodeAssistData} from "../../modals/ai-code-assist/ai-code-assist.component";
import {RebrandInfoComponent} from "../../modals/info/rebrand-info/rebrand-info.component";
import {AiCodeAssistResponse} from "../../interfaces/computation/ai-code-assist.interface";

@Injectable({
  providedIn: 'root'
})
export class CommonModalsService {
  constructor(
    private dialog: MatDialog
  ) { }

  async areYouSure(extraConfirmation: boolean, section: string, detail?: string, toggle?: string) {
    const modalRef = this.dialog.open(AreYouSureComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { extraConfirmation, section, detail, toggle },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async viewComputationExample(computation: ComputationReferenceInterface) {
    const modalRef = this.dialog.open(ComputationExampleComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { computation },
    });
    await firstValueFrom(modalRef.afterClosed());
    return
  }

  async getInTouch() {
    const modalRef = this.dialog.open(GetInTouchComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async suggestComputation() {
    const modalRef = this.dialog.open(SuggestComputationComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async limitReached() {
    const modalRef = this.dialog.open(LimitReachedComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async outputPathInfo() {
    const modalRef = this.dialog.open(OutputPathInfoComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async infoAuthToken(currentToken?: string) {
    const modalRef = this.dialog.open(InfoAuthTokenComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: {
        token: currentToken
      },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async changeTierConfirmation(tier: TierInterface, mode: 'upgrade' | 'downgrade', yearly: boolean) {
    const modalRef = this.dialog.open(ChangeTierConfirmationComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { tier, mode, yearly },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async infoMatchValueOn(platform: string, matches: string[]) {
    const modalRef = this.dialog.open(InfoMatchValueOnComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { platform, matches },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async unwrapInfo() {
    const modalRef = this.dialog.open(InfoUnwrapObjectComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async showErrorModal(error: string, status: number, modal: string, action: string, data: any) {
    const modalRef = this.dialog.open(ErrorModalComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { error, status, modal, action, data },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async howToPostData(section: string, endpoint: string, bearer?: string) {
    const modalRef = this.dialog.open(HowToPostDataComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { section, endpoint, bearer },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  // async computationsTimeoutInfo() {
  //   const modalRef = this.dialog.open(ComputationsTimeoutDisclaimerComponent, {
  //     panelClass: centeredModal,
  //     maxWidth: lg,
  //     data: { },
  //   });
  //   const res = await firstValueFrom(modalRef.afterClosed());
  //   return (res) ? res : null
  // }

  async modalInfoWhatIs(element: string) {
    const modalRef = this.dialog.open(ModalInfoWhatIsComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { element },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async infoSlackChannel() {
    const modalRef = this.dialog.open(InfoSlackChannelComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async rebrandInfo() {
    const modalRef = this.dialog.open(RebrandInfoComponent, {
      panelClass: centeredModal,
      maxWidth: xl,
      data: { },
    });
    await firstValueFrom(modalRef.afterClosed());
    return
  }

  // async bookConsultation() {
  //   const modalRef = this.dialog.open(BookAConsultationComponent, {
  //     panelClass: centeredModal,
  //     maxWidth: lg,
  //     data: { },
  //   });
  //   const res = await firstValueFrom(modalRef.afterClosed());
  //   return (res) ? res : null
  // }

  async infoStandardIntegration(integrationType: string, mode: 'extraction' | 'source' | 'destination') {
    const modalRef = this.dialog.open(InfoStandardIntegrationComponent, {
      panelClass: centeredModal,
      maxWidth: xl,
      data: { integration: integrationType, mode },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async infoAdditionalCostDetails(additionalCost: AdditionalCostsInterface, availableCredits: number = 0, recurrentCredits: number = 0) {
    const modalRef = this.dialog.open(InfoAdditionalCostDetailsComponent, {
      panelClass: centeredModal,
      maxWidth: lg,
      data: {
        additionalCost, availableCredits, recurrentCredits
      },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }

  async aiCodeAssist(data: AiCodeAssistData) {
    const modalRef = this.dialog.open(AiCodeAssistComponent, {
      panelClass: centeredModal,
      maxWidth: xl2,
      data,
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res as AiCodeAssistResponse : null
  }

  async infoBuiltInIntegrationsNotAllowed() {
    const modalRef = this.dialog.open(InfoBuiltInIntegrationsNotAllowedComponent, {
      panelClass: centeredModal,
      maxWidth: xl,
      data: { },
    });
    const res = await firstValueFrom(modalRef.afterClosed());
    return (res) ? res : null
  }
}
